// Client-side image resize + re-encode.
//
// Why: iPad camera photos land at 12MP / ~5–8 MB. Storing thumbnails
// that big is wasteful (fills object storage, sluggish list views) and
// slows uploads over 4G. We resize on-device before the POST so the
// server + storage never see the raw camera file.
//
// resizeImage(file, opts) -> Promise<File>
//   maxSide: longest edge in px (default 1600 — plenty for a thumbnail
//     grid and a full-screen preview on an iPad Pro).
//   quality: 0..1 JPEG quality (default 0.85).
//
// Falls back to the original File on any failure — never blocks the
// upload for a resize error.
export const resizeImage = async (file, opts = {}) => {
  const { maxSide = 1600, quality = 0.85 } = opts;

  // Non-image files (e.g. PDFs) or already-tiny files pass through.
  if (!file || !file.type?.startsWith("image/")) return file;
  if (file.size < 400 * 1024) return file;

  try {
    // createImageBitmap handles EXIF orientation automatically on modern
    // browsers (Safari 15+, Chrome, Firefox) which matters for iPad
    // portrait shots that would otherwise land sideways.
    const bitmap = await createImageBitmap(file, {
      imageOrientation: "from-image",
    });
    const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
    if (scale >= 1) {
      bitmap.close?.();
      return file;
    }
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();

    const blob = await new Promise((res) =>
      canvas.toBlob(res, "image/jpeg", quality),
    );
    if (!blob) return file;

    // Preserve the original stem, force .jpg extension so the server
    // extension sniff produces the correct storage path.
    const stem = (file.name || "photo").replace(/\.[^.]+$/, "");
    return new File([blob], `${stem}.jpg`, {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  } catch (err) {
    // Never block an upload because a resize choked — the server
    // accepts up to 15 MB so the raw photo is still fine as a fallback.
    console.warn("resizeImage failed, uploading original:", err);
    return file;
  }
};
